/** Haversine distance in metres. */
export function distanceMetres(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export type ReverseGeocodeResult = {
  county: string;
  ward: string;
  placeName?: string;
  latitude: number;
  longitude: number;
};

type MapboxFeature = {
  place_name?: string;
  context?: Array<{ id?: string; text?: string }>;
  text?: string;
  place_type?: string[];
};

/** Reverse-geocode via Mapbox (client token from public config). */
export async function reverseGeocodeMapbox(
  latitude: number,
  longitude: number,
  accessToken: string,
): Promise<ReverseGeocodeResult | null> {
  const url = new URL(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json`,
  );
  url.searchParams.set("access_token", accessToken);
  url.searchParams.set("types", "place,locality,neighborhood,district,region");
  url.searchParams.set("country", "ke");
  url.searchParams.set("limit", "1");

  const res = await fetch(url.toString());
  if (!res.ok) return null;
  const data = (await res.json()) as { features?: MapboxFeature[] };
  const feature = data.features?.[0];
  if (!feature) return null;

  const ctx = feature.context || [];
  const region = ctx.find((c) => c.id?.startsWith("region."))?.text || "";
  const district = ctx.find((c) => c.id?.startsWith("district."))?.text || "";
  const place = ctx.find((c) => c.id?.startsWith("place."))?.text || feature.text || "";
  const neighborhood =
    ctx.find((c) => c.id?.startsWith("neighborhood.") || c.id?.startsWith("locality."))?.text || "";

  const county = region || place || "Kenya";
  const ward = neighborhood || district || place || feature.text || "Unknown";

  return {
    county,
    ward,
    ...(feature.place_name ? { placeName: feature.place_name } : {}),
    latitude,
    longitude,
  };
}

export type MapboxSearchHit = {
  id: string;
  placeName: string;
  county?: string;
  ward?: string;
  latitude: number;
  longitude: number;
};

type ScoredHit = MapboxSearchHit & { score: number };

function stripCounty(value: string | undefined) {
  return value?.replace(/\s+county$/i, "").trim() || undefined;
}

function uniqueParts(parts: Array<string | undefined>) {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const value = (part || "").replace(/\s+/g, " ").trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** How well a place title matches what the user has typed. 0 = drop. */
function relevance(placeName: string, query: string) {
  const primary = (placeName.split(",")[0] || placeName).trim().toLowerCase();
  const full = placeName.toLowerCase();
  const q = query.trim().toLowerCase();
  if (!q || !primary) return 0;
  if (primary === q) return 100;
  if (primary.startsWith(q)) return 80;
  if (primary.split(/\s+/).some((token) => token.startsWith(q))) return 70;
  if (primary.includes(q) || full.includes(q)) return 55;
  return 0;
}

function samePlace(a: MapboxSearchHit, b: MapboxSearchHit) {
  const na = (a.placeName.split(",")[0] || "").trim().toLowerCase();
  const nb = (b.placeName.split(",")[0] || "").trim().toLowerCase();
  if (!na || na !== nb) return false;
  return Math.abs(a.latitude - b.latitude) < 0.03 && Math.abs(a.longitude - b.longitude) < 0.03;
}

function linkSignal(parent: AbortSignal | undefined, ms: number) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  const onAbort = () => ctrl.abort();
  parent?.addEventListener("abort", onAbort, { once: true });
  if (parent?.aborted) ctrl.abort();
  return {
    signal: ctrl.signal,
    cancel() {
      clearTimeout(timer);
      parent?.removeEventListener("abort", onAbort);
    },
  };
}

async function searchMapboxIndex(
  query: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<ScoredHit[]> {
  if (!accessToken) return [];
  const linked = linkSignal(signal, 4000);
  try {
    const url = new URL(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
    );
    url.searchParams.set("access_token", accessToken);
    url.searchParams.set("country", "ke");
    url.searchParams.set("autocomplete", "true");
    url.searchParams.set("language", "en");
    url.searchParams.set("limit", "8");
    const res = await fetch(url.toString(), { signal: linked.signal });
    if (!res.ok) return [];
    const data = (await res.json()) as {
      features?: Array<
        MapboxFeature & { id?: string; center?: [number, number]; place_name?: string; text?: string }
      >;
    };
    const hits: ScoredHit[] = [];
    for (const f of data.features || []) {
      const ctx = f.context || [];
      const region = ctx.find((c) => c.id?.startsWith("region."))?.text;
      const place = ctx.find((c) => c.id?.startsWith("place."))?.text || f.text;
      const neighborhood = ctx.find(
        (c) => c.id?.startsWith("neighborhood.") || c.id?.startsWith("locality."),
      )?.text;
      const placeName = f.place_name || f.text || "";
      // Mapbox already ranked this Kenya hit. Keep fuzzy matches, but below an exact name.
      const score = relevance(f.text || placeName, query) || 40;
      if (!placeName) continue;
      hits.push({
        id: f.id || placeName,
        placeName,
        longitude: f.center?.[0] ?? 0,
        latitude: f.center?.[1] ?? 0,
        score,
        ...(stripCounty(region || place) ? { county: stripCounty(region || place) } : {}),
        ...(neighborhood || place || f.text
          ? { ward: neighborhood || place || f.text }
          : {}),
      });
    }
    return hits;
  } catch {
    return [];
  } finally {
    linked.cancel();
  }
}

type MeteoResult = {
  id?: number;
  name?: string;
  latitude?: number;
  longitude?: number;
  country_code?: string;
  admin1?: string;
  admin2?: string;
  admin3?: string;
  admin4?: string;
};

/** GeoNames populated places. Mapbox's Kenya index misses villages such as Mataara. */
async function searchOpenMeteo(query: string, signal?: AbortSignal): Promise<ScoredHit[]> {
  const linked = linkSignal(signal, 4000);
  try {
    const url = new URL("https://geocoding-api.open-meteo.com/v1/search");
    url.searchParams.set("name", query);
    url.searchParams.set("count", "10");
    url.searchParams.set("language", "en");
    url.searchParams.set("countryCode", "KE");
    const res = await fetch(url.toString(), { signal: linked.signal });
    if (!res.ok) return [];
    const data = (await res.json()) as { results?: MeteoResult[] };
    const hits: ScoredHit[] = [];
    for (const row of data.results || []) {
      if (row.country_code && row.country_code.toUpperCase() !== "KE") continue;
      if (row.latitude == null || row.longitude == null || !row.name) continue;
      const county = stripCounty(row.admin1);
      const ward = row.admin4 || row.admin3 || row.admin2 || row.name;
      const placeName = uniqueParts([row.name, row.admin4, row.admin3, row.admin2, row.admin1, "Kenya"]).join(", ");
      const score = relevance(row.name, query);
      if (!score) continue;
      hits.push({
        id: `meteo:${row.id ?? `${row.latitude},${row.longitude}`}`,
        placeName,
        latitude: row.latitude,
        longitude: row.longitude,
        score,
        ...(county ? { county } : {}),
        ...(ward ? { ward } : {}),
      });
    }
    return hits;
  } catch {
    return [];
  } finally {
    linked.cancel();
  }
}

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    osm_id?: number;
    osm_type?: string;
    name?: string;
    countrycode?: string;
    state?: string;
    county?: string;
    city?: string;
    district?: string;
    locality?: string;
  };
};

/** OpenStreetMap names (hamlets, factories, wards) that neither index above has. */
async function searchPhoton(query: string, signal?: AbortSignal): Promise<ScoredHit[]> {
  const linked = linkSignal(signal, 1800);
  try {
    const url = new URL("https://photon.komoot.io/api/");
    url.searchParams.set("q", query);
    url.searchParams.set("limit", "8");
    url.searchParams.set("lang", "en");
    // Bias toward Kenya; country is filtered below. Photon has no country param.
    url.searchParams.set("lat", "-0.5");
    url.searchParams.set("lon", "37.6");
    const res = await fetch(url.toString(), { signal: linked.signal });
    if (!res.ok) return [];
    const data = (await res.json()) as { features?: PhotonFeature[] };
    const hits: ScoredHit[] = [];
    for (const feature of data.features || []) {
      const props = feature.properties || {};
      if ((props.countrycode || "").toUpperCase() !== "KE") continue;
      const [lng, lat] = feature.geometry?.coordinates || [];
      if (lat == null || lng == null || !props.name) continue;
      const score = relevance(props.name, query);
      if (!score) continue;
      const county = stripCounty(props.state);
      const ward = props.city || props.locality || props.district || props.county || props.name;
      hits.push({
        id: `osm:${props.osm_type || "x"}${props.osm_id ?? `${lat},${lng}`}`,
        placeName: uniqueParts([props.name, ward, props.county, county, "Kenya"]).join(", "),
        latitude: lat,
        longitude: lng,
        score,
        ...(county ? { county } : {}),
        ...(ward ? { ward } : {}),
      });
    }
    return hits;
  } catch {
    return [];
  } finally {
    linked.cancel();
  }
}

function mergeHits(groups: ScoredHit[][], limit: number): MapboxSearchHit[] {
  const kept: ScoredHit[] = [];
  for (const hit of groups.flat()) {
    const existing = kept.find((row) => samePlace(row, hit));
    if (!existing) {
      kept.push(hit);
      continue;
    }
    if (hit.score > existing.score || (hit.score === existing.score && hit.placeName.length > existing.placeName.length)) {
      const index = kept.indexOf(existing);
      kept[index] = hit;
    }
  }
  kept.sort((a, b) => b.score - a.score || a.placeName.length - b.placeName.length);
  return kept.slice(0, limit).map(({ score: _score, ...hit }) => hit);
}

/**
 * Live place search for Kenya.
 * Mapbox alone misses many villages (Mataara returns zero). Open-Meteo covers
 * populated places; Photon adds OpenStreetMap names. Results are ranked by
 * how closely the name matches the typed text.
 */
export async function searchPlacesMapbox(
  query: string,
  accessToken: string,
  limit = 5,
  signal?: AbortSignal,
): Promise<MapboxSearchHit[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  if (signal?.aborted) return [];
  const [mapbox, meteo, photon] = await Promise.all([
    searchMapboxIndex(q, accessToken, signal),
    searchOpenMeteo(q, signal),
    searchPhoton(q, signal),
  ]);
  if (signal?.aborted) return [];
  return mergeHits([mapbox, meteo, photon], limit);
}

export const LIVE_STALE_MS = 2 * 60 * 60 * 1000;
export const LIVE_THROTTLE_MS = 5 * 60 * 1000;
export const LIVE_MOVE_METRES = 250;
